import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import api from '../../../services/api';
import { useSnackbar } from '../../../components/common/Snackbar';

/**
 * Hook to manage projects data for Users page
 * @param {Object} params - Hook parameters
 * @param {string} params.selectedRegion - Selected region for filtering
 * @param {Array} params.formProjects - Current form projects selection
 * @param {Function} params.setFormProjects - Setter for form projects
 * @param {string} params.selectedRole - Selected role in form
 * @returns {Object} Projects state and functions
 */
export const useUsersProjects = ({ selectedRegion, formProjects, setFormProjects, selectedRole }) => {
  const { showSnackbar } = useSnackbar();
  const [allProjects, setAllProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const prevRegionRef = useRef(selectedRegion);

  const fetchProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const response = await api.get('/projects', {
        params: {
          limit: 1000, // Get all projects
        },
      });

      let allProjectsData = [];
      if (response.data && response.data.data) {
        allProjectsData = response.data.data.projects || [];
      } else if (response.data && Array.isArray(response.data)) {
        allProjectsData = response.data;
      } else if (response.data && response.data.projects) {
        allProjectsData = response.data.projects;
      }

      if (!Array.isArray(allProjectsData)) {
        allProjectsData = [];
      }

      // Store all projects with their region information
      setAllProjects(allProjectsData);

    } catch (error) {
      // console.error('Failed to fetch projects:', error);
      showSnackbar('Failed to fetch projects', 'error');
      setAllProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, [showSnackbar]);

  const projects = useMemo(() => {
    if (!allProjects || allProjects.length === 0) return [];

    const selectedRegionValue = selectedRegion;
    const baseProjects = allProjects.filter((project) => {
      if (!selectedRegionValue || selectedRegionValue === '' || selectedRegionValue === 'ALL') {
        return true;
      }
      return project?.region === selectedRegionValue;
    });

    const mappedProjects = baseProjects
      .map((project) => {
        const name = project?.name || project?.projectName || '';
        const code = project?.code || '';
        const id = project?._id || project?.id || '';
        const uniqueValue = id || code || name;

        return {
          value: uniqueValue,
          label: code ? `${name} (${code})` : name,
          name,
          code,
          region: project?.region,
        };
      })
      .filter((proj) => proj.value && proj.name);

    const uniqueByValue = Array.from(
      new Map(mappedProjects.map((project) => [project.value, project])).values()
    );

    return uniqueByValue.sort((a, b) => {
      const nameSort = a.name.localeCompare(b.name);
      if (nameSort !== 0) return nameSort;
      return (a.code || '').localeCompare(b.code || '');
    });
  }, [allProjects, selectedRegion]);

  // Reset selected projects when region changes
  useEffect(() => {
    const isGlobalRole = selectedRole === 'CH' || selectedRole === 'MP';
    if (isGlobalRole) {
      prevRegionRef.current = selectedRegion;
      return; // CH/MP projects are handled by the auto-select effect below
    }

    if (prevRegionRef.current !== selectedRegion) {
      prevRegionRef.current = selectedRegion;
      // Clear previously selected projects when region changes
      if (formProjects && formProjects.length > 0) {
        setFormProjects([]);
      }
    }
  }, [selectedRegion, selectedRole, formProjects, setFormProjects]);

  // For CH/MP, default to all projects selected when projects are available.
  useEffect(() => {
    const isGlobalRole = selectedRole === 'CH' || selectedRole === 'MP';
    if (!isGlobalRole) return;
    if (!projects || projects.length === 0) return;

    const allProjectValues = projects.map((project) => project.value);
    const sameLength = (formProjects || []).length === allProjectValues.length;
    const sameValues =
      sameLength && allProjectValues.every((value) => (formProjects || []).includes(value));

    if (!sameValues) {
      setFormProjects(allProjectValues);
    }
  }, [selectedRole, projects, formProjects, setFormProjects]);

  // Clear selected projects if they don't belong to the new region
  useEffect(() => {
    if (!formProjects || formProjects.length === 0) return;
    if (!selectedRegion || selectedRegion === '' || selectedRegion === 'ALL') return;
    if (!projects || projects.length === 0) return;

    const allowed = new Set(projects.map((project) => project.value));
    const validProjects = formProjects.filter((projectValue) => allowed.has(projectValue));
    const hasChanges =
      validProjects.length !== formProjects.length ||
      validProjects.some((value, index) => value !== formProjects[index]);

    if (hasChanges) {
      setFormProjects(validProjects);
    }
  }, [formProjects, projects, selectedRegion, setFormProjects]);

  return {
    projects,
    allProjects,
    loadingProjects,
    fetchProjects,
  };
};
